import { useCallback, useRef, useState, Dispatch, SetStateAction } from 'react'
import type { ContactParsed } from 'app/services/sfAuth/sfData/sfContact'
import { isNil } from 'lodash'

export type ContactDialogType = 'contact-picker' | 'project-affiliations'

type ContactFieldsType =
  | 'contact-info'
  | 'update-contact'
  | 'missing-info'
  | 'add-contact'
  | 'search'
  | 'search-results'
  | 'error-results'
  | 'too-many-results'
  | 'new-contact'
  | undefined

interface SearchValues {
  firstName?: string
  lastName?: string
  email?: string
  Id?: string
  Name?: string
  Email__c?: string
  Phone__c?: string
}

export type SearchContactsFn = (params: {
  values: SearchValues
}) => Promise<{ contacts: ContactParsed[], error: string | null }>

interface UseContactDialogProps {
  onSelect?: (contact: ContactParsed) => void
  searchContacts: SearchContactsFn
  createNewContact: (contact: ContactParsed) => Promise<any>
  updateContact: (params: { contact: ContactParsed, isValid: boolean, initialValues: ContactParsed }) => Promise<any>
}

interface UseContactDialogState {
  dialogOpened: boolean
  loading: boolean
  searchResult: any
  anchorEl: HTMLElement | undefined
  searchFirstName: string
  searchLastName: string
  searchEmail: string
  addContactValues: ContactParsed | undefined
  contactFieldsType: ContactFieldsType
  confirmSwitchDialogOpen: boolean
}

interface UseContactDialogActions {
  setAnchorEl: Dispatch<SetStateAction<HTMLElement | undefined>>
  handleReturn: () => void
  confirmSelection: (params: { contact: ContactParsed, isValid: boolean }) => void
  handleSearch: (values: SearchValues) => Promise<void>
  handleUpdate: (params: { values: ContactParsed, isValid: boolean, initialValues: ContactParsed }) => Promise<void>
  handleAddContact: (params: { values: ContactParsed, isValid: boolean, initialValues: ContactParsed, dirty: boolean }) => Promise<void>
  handleEdit: (isMissingInfo: boolean) => void
  handleSwitchContact: (params?: { dirty: boolean }) => void
  handleClose: () => void
  handleModifySearch: () => void
  resultsCreateNewContact: () => void
  handleCreateNewContact: (params: { contact: ContactParsed, isValid: boolean }) => Promise<void>
  handleConfirmSwitchContact: (isConfirmed: boolean) => void
  openDialogWithType: (type: ContactFieldsType) => void
  handleContinueWithSearch: () => void
}

interface UseContactDialogReturn {
  state: UseContactDialogState
  actions: UseContactDialogActions
}

export function useContactDialog ({
  onSelect,
  searchContacts,
  createNewContact,
  updateContact
}: UseContactDialogProps): UseContactDialogReturn {
  const [dialogOpened, setDialogOpened] = useState(false)
  const [loading, setLoading] = useState(false)
  const [searchResult, setSearchResult] = useState<any>(null)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | undefined>()

  const [searchFirstName, setSearchFirstName] = useState('')
  const [searchLastName, setSearchLastName] = useState('')
  const [searchEmail, setSearchEmail] = useState('')

  const [addContactValues, setAddContactValues] = useState<ContactParsed | undefined>()
  const [contactFieldsType, _setContactFieldsType] = useState<ContactFieldsType>()
  const [confirmSwitchDialogOpen, setConfirmSwitchDialogOpen] = useState(false)

  const prevContactFieldsType = useRef<ContactFieldsType[]>([])

  const setContactFieldsType = (type: ContactFieldsType): void => {
    _setContactFieldsType(prevType => {
      if (prevType !== 'error-results' && prevType !== 'too-many-results' && prevType !== undefined) {
        prevContactFieldsType.current = [...prevContactFieldsType.current, prevType]
      }
      const lastPrevType = prevContactFieldsType.current[prevContactFieldsType.current.length - 1]
      if (type === 'error-results' && lastPrevType === 'search-results') {
        prevContactFieldsType.current = prevContactFieldsType.current.slice(0, -1)
      }
      return type
    })
  }

  const handleReturn = useCallback(() => {
    if (prevContactFieldsType.current.length === 0) {
      setDialogOpened(false)
    }
    let lastType = prevContactFieldsType.current.pop()
    while (lastType === contactFieldsType) {
      lastType = prevContactFieldsType.current.pop()
    }
    if (lastType === undefined) {
      setDialogOpened(false)
    } else {
      _setContactFieldsType(lastType)
    }
  }, [contactFieldsType])

  const confirmSelection = useCallback(({ contact, isValid }: { contact: ContactParsed, isValid: boolean }) => {
    if (onSelect != null && isValid) {
      onSelect({
        ...contact
        // relationship: true
      })
    }
    if (isValid) {
      _setContactFieldsType('contact-info')
      setDialogOpened(false)
    } else {
      setAddContactValues(contact)
      setSearchFirstName('')
      setSearchLastName('')
      setSearchEmail('')
      setContactFieldsType('add-contact')
    }
  }, [onSelect, setContactFieldsType])

  const handleSearch = useCallback(async (values: SearchValues) => {
    setLoading(true)
    setSearchResult(null)
    setContactFieldsType('search-results')

    const { contacts, error } = await searchContacts({ values })

    setLoading(false)
    setSearchFirstName(values.firstName ?? '')
    setSearchLastName(values.lastName ?? '')
    setSearchEmail(values.email ?? '')

    if (!isNil(error)) {
      setSearchResult(error)
      if (error === 'MULTIPLE_MATCHES') {
        setContactFieldsType('too-many-results')
      } else {
        setContactFieldsType('error-results')
      }
    } else {
      setSearchResult(contacts)
      setContactFieldsType('search-results')
    }
  }, [setContactFieldsType, searchContacts])

  const handleUpdate = useCallback(async ({ values, isValid, initialValues }: { values: ContactParsed, isValid: boolean, initialValues: ContactParsed }) => {
    setLoading(true)
    try {
      await updateContact({ contact: values, isValid, initialValues })
      setLoading(false)
      onSelect?.(values)
      setDialogOpened(false)
      _setContactFieldsType('contact-info')
      setAddContactValues(undefined)
    } catch (error) {
      console.error('Error updating contact:', error)
      setLoading(false)
    }
  }, [onSelect])

  const handleAddContact = useCallback(async ({ values, isValid, initialValues, dirty }: { values: ContactParsed, isValid: boolean, initialValues: ContactParsed, dirty: boolean }) => {
    if (dirty) {
      await handleUpdate({ values, isValid, initialValues })
    } else {
      onSelect?.({
        ...values
        // relationship: true
      })
      setDialogOpened(false)
      _setContactFieldsType('contact-info')
      setAddContactValues(undefined)
    }
  }, [onSelect])

  const handleEdit = useCallback((isMissingInfo: boolean) => {
    if (isMissingInfo) {
      setContactFieldsType('missing-info')
    } else {
      setContactFieldsType('update-contact')
    }
  }, [setContactFieldsType])

  const _handleSwitchContact = useCallback(() => {
    setSearchResult(null)
    setContactFieldsType('search')
  }, [setContactFieldsType])

  const handleSwitchContact = useCallback(({ dirty }: { dirty: boolean } | undefined = { dirty: false }) => {
    if ((contactFieldsType === 'update-contact' || contactFieldsType === 'missing-info') && dirty) {
      setConfirmSwitchDialogOpen(true)
    } else {
      _handleSwitchContact()
    }
  }, [setConfirmSwitchDialogOpen, _handleSwitchContact, contactFieldsType])

  const handleClose = useCallback(() => {
    setDialogOpened(false)
    setTimeout(() => {
      setSearchResult(null)
      setSearchFirstName('')
      setSearchLastName('')
      setSearchEmail('')
      setAddContactValues(undefined)
      _setContactFieldsType('search')
    }, 0)
  }, [])

  const handleModifySearch = useCallback(() => {
    setContactFieldsType('search')
  }, [setContactFieldsType])

  const resultsCreateNewContact = useCallback(() => {
    // setSearchResult(null)
    setContactFieldsType('new-contact')
  }, [setContactFieldsType])

  const handleCreateNewContact = useCallback(async ({ contact, isValid }: { contact: ContactParsed, isValid: boolean }) => {
    setLoading(true)
    try {
      await createNewContact(contact)
      setDialogOpened(false)
    } catch (error) {
      console.error('Error creating new contact:', error)
    }
    setLoading(false)
  }, [setContactFieldsType])

  const openDialogWithType = useCallback((type: ContactFieldsType) => {
    setContactFieldsType(type)
    setDialogOpened(true)
  }, [setContactFieldsType])

  const handleConfirmSwitchContact = useCallback((isConfirmed: boolean) => {
    if (isConfirmed) {
      _handleSwitchContact()
    }
    setConfirmSwitchDialogOpen(false)
  }, [_handleSwitchContact])

  const handleContinueWithSearch = useCallback(() => {
    setContactFieldsType('search')
  }, [setContactFieldsType])

  return {
    state: {
      dialogOpened,
      loading,
      searchResult,
      anchorEl,
      searchFirstName,
      searchLastName,
      searchEmail,
      addContactValues,
      contactFieldsType,
      confirmSwitchDialogOpen
    },
    actions: {
      setAnchorEl,
      handleReturn,
      confirmSelection,
      handleAddContact,
      handleSearch,
      handleUpdate,
      handleEdit,
      handleSwitchContact,
      handleClose,
      handleModifySearch,
      resultsCreateNewContact,
      handleCreateNewContact,
      openDialogWithType,
      handleConfirmSwitchContact,
      handleContinueWithSearch
    }
  }
}
